import { useEffect } from "react";
import { Link } from "react-router-dom";
import "./PrivacyPolicyPage.css";

const TermsAndConditionsPage = () => {
  useEffect(() => {
    document.title = "Terms and Conditions - Orindra";
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        {/* Header */}
        <header className="legal-header">
          <div className="legal-header-content">
            <Link to="/" className="legal-logo">
              <img src="/orindra-logo.png" alt="Orindra logo" />
            </Link>
            <nav className="legal-nav">
              <Link to="/" className="legal-nav-link">
                Back to Home
              </Link>
            </nav>
          </div>
        </header>

        {/* Main Content */}
        <main className="legal-main">
          <div className="legal-content">
            <h1 className="legal-title">TERMS AND CONDITIONS</h1>

            <div className="legal-meta">
              <p>
                <strong>Elvis Tapfumanei (PTY) Ltd</strong>
              </p>
              <p>
                <strong>Registration Number:</strong> 2024/123610/07
              </p>
              <p>
                <strong>Address:</strong> Cape Town, South Africa
              </p>
              <p>
                <strong>Effective Date:</strong> 2025-01-01
              </p>
              <p>
                <strong>Last Updated:</strong> 2025-01-01
              </p>
            </div>

            <hr className="legal-divider" />

            <section className="legal-section">
              <h2>1. INTRODUCTION AND ACCEPTANCE</h2>

              <h3>1.1 Agreement Formation</h3>
              <p>
                These Terms and Conditions ("<strong>Terms</strong>", "
                <strong>Agreement</strong>") constitute a legally binding
                contract between you ("<strong>User</strong>", "
                <strong>you</strong>", or "<strong>your</strong>") and Elvis
                Tapfumanei (PTY) Ltd, a company incorporated under the laws of
                South Africa with registration number 2024/123610/07 ("
                <strong>Company</strong>", "<strong>we</strong>", "
                <strong>us</strong>", or "<strong>our</strong>"), governing your
                access to and use of the Orindra platform ("
                <strong>Service</strong>", "<strong>Platform</strong>", or "
                <strong>Application</strong>").
              </p>

              <h3>1.2 Acceptance of Terms</h3>
              <p>
                By accessing, downloading, installing, or using the Service, you
                acknowledge that you have read, understood, and agree to be
                bound by these Terms and our Privacy Policy, which is
                incorporated herein by reference. If you do not agree to these
                Terms, you must not access or use the Service.
              </p>

              <h3>1.3 Legal Capacity</h3>
              <p>You represent and warrant that:</p>
              <ul>
                <li>
                  You are at least 18 years of age or the age of majority in
                  your jurisdiction
                </li>
                <li>
                  You have the legal capacity to enter into this Agreement
                </li>
                <li>
                  You are not prohibited from using the Service under applicable
                  laws
                </li>
                <li>
                  If accepting on behalf of an organization, you have authority
                  to bind that organization
                </li>
              </ul>

              <h3>1.4 Governing Law</h3>
              <p>
                This Agreement is governed by the laws of South Africa, without
                regard to conflict of law principles. Any disputes shall be
                subject to the exclusive jurisdiction of South African courts.
              </p>
            </section>

            <section className="legal-section">
              <h2>2. SERVICE DESCRIPTION</h2>

              <h3>2.1 Platform Overview</h3>
              <p>
                Orindra is an intelligent meeting notes application that
                provides:
              </p>
              <ul>
                <li>Real-time audio recording and transcription services</li>
                <li>AI-powered meeting analysis and insights</li>
                <li>
                  Automated generation of meeting summaries, action items, and
                  decisions
                </li>
                <li>Meeting management and collaboration tools</li>
                <li>
                  Integration with third-party calendar and productivity
                  applications
                </li>
              </ul>

              <h3>2.2 Service Components</h3>
              <div className="legal-subsection">
                <h4>Core Features:</h4>
                <ul>
                  <li>Audio recording and storage</li>
                  <li>Speech-to-text transcription</li>
                  <li>AI content analysis and summarization</li>
                  <li>Meeting participant management</li>
                  <li>Template-based meeting organization</li>
                  <li>Real-time collaboration tools</li>
                </ul>
              </div>

              <div className="legal-subsection">
                <h4>AI-Powered Features:</h4>
                <ul>
                  <li>Sentiment analysis</li>
                  <li>Theme identification</li>
                  <li>Action item extraction</li>
                  <li>Decision tracking</li>
                  <li>Meeting insights and analytics</li>
                  <li>Automated follow-up generation</li>
                </ul>
              </div>

              <h3>2.3 Service Availability</h3>
              <ul>
                <li>The Service is provided on an "as available" basis</li>
                <li>
                  We strive for 99.9% uptime but do not guarantee uninterrupted
                  service
                </li>
                <li>
                  Scheduled maintenance will be communicated in advance when
                  possible
                </li>
                <li>Emergency maintenance may occur without prior notice</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>3. USER ACCOUNTS AND REGISTRATION</h2>

              <h3>3.1 Account Creation</h3>
              <div className="legal-subsection">
                <h4>Registration Requirements:</h4>
                <ul>
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your account information</li>
                  <li>
                    Choose a secure password and protect your login credentials
                  </li>
                  <li>Verify your email address as required</li>
                </ul>
              </div>

              <div className="legal-subsection">
                <h4>Prohibited Registrations:</h4>
                <ul>
                  <li>False or misleading information</li>
                  <li>Impersonation of another person or entity</li>
                  <li>Use of automated registration methods</li>
                  <li>
                    Multiple accounts for the same individual (without
                    authorization)
                  </li>
                </ul>
              </div>

              <h3>3.2 Account Security</h3>
              <div className="legal-subsection">
                <h4>Your Responsibilities:</h4>
                <ul>
                  <li>
                    Maintain the confidentiality of your account credentials
                  </li>
                  <li>Notify us immediately of any unauthorized access</li>
                  <li>Use strong, unique passwords</li>
                  <li>Enable two-factor authentication when available</li>
                  <li>Log out from shared or public devices</li>
                </ul>
              </div>

              <div className="legal-subsection">
                <h4>Our Responsibilities:</h4>
                <ul>
                  <li>Implement industry-standard security measures</li>
                  <li>Encrypt sensitive data in transit and at rest</li>
                  <li>Monitor for suspicious account activity</li>
                  <li>Provide security notifications and recommendations</li>
                </ul>
              </div>
            </section>

            <section className="legal-section">
              <h2>4. ACCEPTABLE USE POLICY</h2>

              <h3>4.1 Permitted Uses</h3>
              <p>You may use the Service to:</p>
              <ul>
                <li>Record and transcribe legitimate business meetings</li>
                <li>Generate meeting summaries and action items</li>
                <li>Collaborate with authorized meeting participants</li>
                <li>Integrate with approved third-party applications</li>
                <li>Access and export your own meeting data</li>
              </ul>

              <h3>4.2 Prohibited Uses</h3>
              <div className="legal-subsection">
                <h4>Strictly Prohibited Activities:</h4>

                <h5>Illegal Activities:</h5>
                <ul>
                  <li>Recording meetings without proper consent</li>
                  <li>Violating privacy laws or regulations</li>
                  <li>Infringing intellectual property rights</li>
                  <li>Engaging in fraudulent activities</li>
                  <li>Money laundering or terrorist financing</li>
                </ul>

                <h5>Harmful Content:</h5>
                <ul>
                  <li>Uploading malicious software or code</li>
                  <li>Distributing spam or unsolicited communications</li>
                  <li>Sharing defamatory, harassing, or threatening content</li>
                  <li>Posting discriminatory or hate speech content</li>
                  <li>
                    Distributing sexually explicit or inappropriate material
                  </li>
                </ul>

                <h5>System Abuse:</h5>
                <ul>
                  <li>Attempting to gain unauthorized access</li>
                  <li>Reverse engineering or decompiling the Service</li>
                  <li>Interfering with Service operations</li>
                  <li>Overloading or disrupting Service infrastructure</li>
                  <li>Circumventing security measures or usage limits</li>
                </ul>
              </div>
            </section>

            <section className="legal-section">
              <h2>5. SUBSCRIPTION PLANS AND BILLING</h2>

              <h3>5.1 Subscription Tiers</h3>
              <div className="legal-subsection">
                <h4>Free Tier:</h4>
                <ul>
                  <li>Limited meeting duration and storage</li>
                  <li>Basic transcription and analysis features</li>
                  <li>Standard support</li>
                  <li>Advertisement-supported (if applicable)</li>
                </ul>
              </div>

              <div className="legal-subsection">
                <h4>Premium Tiers:</h4>
                <ul>
                  <li>Extended meeting duration and storage</li>
                  <li>Advanced AI analysis features</li>
                  <li>Priority support</li>
                  <li>Additional integrations and customization options</li>
                </ul>
              </div>

              <h3>5.2 Billing Terms</h3>
              <div className="legal-subsection">
                <h4>Payment Processing:</h4>
                <ul>
                  <li>Subscriptions are billed in advance</li>
                  <li>
                    Payments are processed through secure third-party providers
                  </li>
                  <li>All fees are exclusive of applicable taxes</li>
                  <li>
                    Currency conversion rates apply for international payments
                  </li>
                </ul>
              </div>

              <h3>5.3 Price Changes</h3>
              <ul>
                <li>30 days advance notice for subscription price increases</li>
                <li>Immediate effect for new features or services</li>
                <li>
                  Grandfathering of existing subscriptions for reasonable
                  periods
                </li>
                <li>Option to cancel before price increases take effect</li>
              </ul>
            </section>

            <div className="legal-footer">
              <p>
                <strong>Document Version:</strong> 1.0
              </p>
              <p>
                <strong>Effective Date:</strong> 2025-01-01
              </p>
              <p>
                <strong>Last Updated:</strong> 2025-01-01
              </p>
              <p>
                <strong>Next Review Date:</strong> 2026-01-01
              </p>
            </div>

            <div className="legal-disclaimer">
              <p>
                <em>
                  These Terms and Conditions have been prepared to comply with
                  South African law and international best practices. This
                  document is designed to provide comprehensive legal protection
                  while maintaining fair and transparent user relationships. For
                  specific legal advice regarding your situation, please consult
                  with qualified legal counsel.
                </em>
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="legal-footer-section">
          <div className="legal-footer-content">
            <div>
              © {new Date().getFullYear()} Orindra. Built for teams who refuse
              to waste time.
            </div>
            <div className="legal-footer-links">
              <Link to="/privacy" className="legal-footer-link">
                Privacy
              </Link>
              <Link to="/terms" className="legal-footer-link">
                Terms
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
